import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  CurrentUserVacationStyled,
  MobileCalendarCell,
  MobileCalendarContainer,
  MobileCalendarDrawer,
  MobileCalendarOverlay,
  MobileCalendarOverlayHeader,
  MobileCalendarRow,
  MobileCalendarTable,
  TeamVacationList,
  TeamVacationListItem,
  VacationDate,
} from '../styles/MobileCalendar.styled';
import { TeamVacationData } from 'src/tabs/hooks/useGetTeamRequests';
import { requestDatesRange } from '../utils/requestDatesRange';
import { useBoolean } from '@fluentui/react-hooks';
import { RequestIcon } from '../../RequestsWidget/RequestIcon';
import { ChevronDown48Regular } from '@fluentui/react-icons';
import { formatDate } from 'src/utils/dateFns/formatDate';
import { TeamVacationCircle } from 'src/components/Timeoff/TeamVacationCircle/TeamVacationCircle';
import { AvatarCircle } from 'src/components/Timeoff/TeamVacationCircle/styles/TeamVacationCircle.styled';
import { DATE_FILTER_CRITERIA } from 'src/tabs/constants/Timeoff.constants';
import { DEFAULT_DATE_FORMAT } from 'src/tabs/constants/Common.constants';
import {
  generateReactKey,
  KeyRoots,
} from 'src/utils/generateUniqueReactKey/generateUniqueReactKey';

interface MobileCalendarProps {
  month: number;
  year: number;
  restrictedDates: Date[];
  requests: Array<TeamVacationData>;
  mappedCurrentUserRequests: {
    name: string;
    status: string;
    startDate: string;
    endDate: string;
    vacation_type: string;
  }[];
  hideEmployees: boolean;
}

interface VacationType {
  currentUser: {
    dates: Set<string>;
    endDate: string;
    name: string;
    startDate: string;
    status: string;
    vacation_type: string;
  };
  employeeVacationsArray: Array<{
    backgroundPersona: string;
    colorPersona: string;
    dates: Set<string>;
    employeeId: string;
    endDate: string;
    name: string;
    startDate: string;
  }>;
  date: string;
}

export const MobileCalendar = ({
  month,
  year,
  restrictedDates,
  mappedCurrentUserRequests,
  requests,
  hideEmployees,
}: MobileCalendarProps) => {
  const [isPopupVisible, { setTrue: showPopup, setFalse: hidePopup }] =
    useBoolean(false);
  const [selectedVacation, setSelectedVacation] = useState<VacationType>();
  const {
    year: yearStr,
    month: monthStr,
    day: dayStr,
  } = formatDate(
    selectedVacation ? new Date(selectedVacation.date) : new Date()
  );
  const requestsRange = !hideEmployees ? requestDatesRange(requests) : [];
  const currentUserRequestsRange = requestDatesRange(mappedCurrentUserRequests);
  const weekends = restrictedDates.map((date) =>
    moment(date).format(DEFAULT_DATE_FORMAT)
  );
  const date = new Date(year, month, 1);
  const startOfMonth = moment(date).startOf(DATE_FILTER_CRITERIA);
  const endOfMonth = moment(date).endOf(DATE_FILTER_CRITERIA);
  const weeks = [];
  const day = startOfMonth.clone().startOf('isoWeek');
  let week = [];

  while (day.isBefore(endOfMonth)) {
    week.push(day.clone());
    day.add(1, 'day');

    if (day.isoWeekday() === 1) {
      weeks.push(week);
      week = [];
    }
  }

  if (week.length > 0) {
    weeks.push(week);
  }

  const handleCalendarCellClick = (
    employeesVacations: VacationType['employeeVacationsArray'],
    currentUserVacations: VacationType['currentUser'],
    formattedDay: VacationType['date']
  ) => {
    if (employeesVacations.length > 0 || currentUserVacations) {
      setSelectedVacation({
        currentUser: currentUserVacations,
        employeeVacationsArray: employeesVacations,
        date: formattedDay,
      });
      showPopup();
    }
  };

  useEffect(() => {
    if (isPopupVisible) {
      document.getElementById('time-off').style.overflow = 'hidden';
    } else {
      document.getElementById('time-off').style.overflow = 'auto';
    }
  }, [isPopupVisible]);

  return (
    <>
      <MobileCalendarContainer>
        <MobileCalendarTable>
          <tbody>
            <th>M</th>
            <th>T</th>
            <th>W</th>
            <th>T</th>
            <th>F</th>
            <th>S</th>
            <th>S</th>
            {weeks.map((week, index) => (
              <MobileCalendarRow
                key={generateReactKey(
                  KeyRoots.TIME_OFF_MOBILE_CALENDAR_ROW,
                  index
                )}
              >
                {week.map((day, index) => {
                  if (day && day.isSame(startOfMonth, DATE_FILTER_CRITERIA)) {
                    const formattedDay = day.format(DEFAULT_DATE_FORMAT);
                    const isRestricted = weekends.includes(formattedDay);
                    const employeesVacations = requestsRange.filter(
                      (employeeRequest) => {
                        if (
                          employeeRequest.dates.has(
                            new Date(formattedDay).toString()
                          )
                        ) {
                          return employeeRequest;
                        }
                      }
                    );
                    const currentUserVacations = currentUserRequestsRange.find(
                      (currentUserRquests) => {
                        if (
                          currentUserRquests.dates.has(
                            new Date(formattedDay).toString()
                          )
                        ) {
                          return currentUserRquests;
                        }
                      }
                    );

                    return (
                      <MobileCalendarCell
                        key={generateReactKey(
                          KeyRoots.MOBILE_CALENDAR_CELL,
                          index
                        )}
                        disabled={isRestricted}
                        currentUserVacations={!!currentUserVacations}
                        onClick={() =>
                          handleCalendarCellClick(
                            employeesVacations,
                            currentUserVacations,
                            formattedDay
                          )
                        }
                      >
                        {employeesVacations.length > 0 && (
                          <>
                            <TeamVacationCircle
                              key={generateReactKey(
                                KeyRoots.TEAM_VACATION_CIRCLE,
                                index
                              )}
                              month={month}
                              employees={employeesVacations}
                              employeesOverflow={employeesVacations}
                              date={formattedDay}
                              currentUser={currentUserVacations || { name: '' }}
                              target={undefined}
                            />
                          </>
                        )}
                        <span>{day.format('D')}</span>
                      </MobileCalendarCell>
                    );
                  } else if (
                    day &&
                    !day.isSame(startOfMonth, DATE_FILTER_CRITERIA)
                  ) {
                    return (
                      <td
                        key={generateReactKey(
                          KeyRoots.MOBILE_CALENDAR_CELL_EMPTY,
                          index
                        )}
                        style={{
                          background: 'transparent',
                        }}
                      ></td>
                    );
                  }
                })}
              </MobileCalendarRow>
            ))}
          </tbody>
        </MobileCalendarTable>
      </MobileCalendarContainer>
      {isPopupVisible && (
        <MobileCalendarDrawer
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              hidePopup();
            }
          }}
        >
          <MobileCalendarOverlay>
            <MobileCalendarOverlayHeader>
              <VacationDate>
                <ChevronDown48Regular onClick={hidePopup} />
                {`${dayStr}/${monthStr}/${yearStr}`}
              </VacationDate>
              {selectedVacation.currentUser?.name !== '' && (
                <CurrentUserVacationStyled>
                  <RequestIcon
                    requestStatus={selectedVacation.currentUser?.status || ''}
                    size={24}
                  />
                  <p>{selectedVacation.currentUser?.vacation_type}</p>
                </CurrentUserVacationStyled>
              )}
            </MobileCalendarOverlayHeader>
            <div>
              <TeamVacationList>
                {selectedVacation.employeeVacationsArray.length > 0 &&
                  selectedVacation.employeeVacationsArray?.map((employee) => {
                    const nameArray = employee.name.split(' ');
                    const initials = nameArray[1]
                      ? nameArray[0][0] + nameArray[1][0]
                      : nameArray[0][0];
                    return (
                      <TeamVacationListItem key={employee.name}>
                        <AvatarCircle
                          background={employee.backgroundPersona}
                          color={employee.colorPersona}
                        >
                          <p>{initials}</p>
                        </AvatarCircle>
                        <li>{employee.name}</li>
                      </TeamVacationListItem>
                    );
                  })}
              </TeamVacationList>
            </div>
          </MobileCalendarOverlay>
        </MobileCalendarDrawer>
      )}
    </>
  );
};

export default MobileCalendar;
